import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Fab, Drawer, Box, TextField, IconButton, Typography, Paper,
  Chip, Collapse
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import CodeIcon from "@mui/icons-material/Code";
import { postChatQuery } from "../../../api/chat";

interface Message {
  role: "user" | "assistant";
  content: string;
  sql?: string;
}

const SUGGESTED_PROMPTS = [
  "Show me my top 5 customers by total PO value",
  "What's my revenue trend over the last 6 months?",
  "Which customers haven't placed a PO in 60+ days?",
  "How many RFQs went unquoted this week?",
  "What's the current price for an HP 26A toner?",
  "Show me all marine-grade monitors and their prices",
  "What's the cheapest UPS we carry?",
  "Search for Cat6 cables in our pricing database",
];

/** One message: who said it, its text (with **bold**), and the SQL behind it. */
function ChatMessage({ msg, sqlOpen, onToggleSql }: { msg: Message; sqlOpen: boolean; onToggleSql: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: msg.role === "user" ? "flex-end" : "flex-start",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
        {msg.role === "assistant" && (
          <SmartToyIcon fontSize="small" color="primary" />
        )}
        {msg.role === "user" && (
          <PersonIcon fontSize="small" color="action" />
        )}
        <Typography variant="caption" color="text.secondary">
          {msg.role === "user" ? "You" : "AI Analyst"}
        </Typography>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          maxWidth: "90%",
          bgcolor:
            msg.role === "user" ? "primary.main" : "background.paper",
          color:
            msg.role === "user" ? "primary.contrastText" : "text.primary",
          borderRadius: 2,
          border: msg.role === "assistant" ? 1 : 0,
          borderColor: "divider",
          lineHeight: 1.6,
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {msg.content
            .split(/(\*\*.*?\*\*)/g)
            .map((part, i) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <strong key={i}>{part.slice(2, -2)}</strong>
              ) : (
                part
              )
            )}
        </Typography>
        {msg.sql && (
          <Box sx={{ mt: 1 }}>
            <Chip
              icon={<CodeIcon />}
              label={sqlOpen ? "Hide SQL" : "View SQL"}
              size="small"
              variant="outlined"
              onClick={onToggleSql}
              sx={{ fontSize: "0.75rem" }}
            />
            <Collapse in={sqlOpen}>
              <Paper
                variant="outlined"
                sx={{
                  mt: 0.5,
                  p: 1,
                  bgcolor: "grey.900",
                  color: "grey.100",
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: 240,
                }}
              >
                {msg.sql}
              </Paper>
            </Collapse>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

/** Three pulsing dots while the analyst is answering. */
function TypingIndicator() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 1 }}>
      <SmartToyIcon fontSize="small" color="primary" />
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "primary.main",
              opacity: 0.4,
              animation: "pulse 1s infinite",
              animationDelay: `${i * 0.2}s`,
              "@keyframes pulse": {
                "0%, 100%": { opacity: 0.2 },
                "50%": { opacity: 0.8 },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

const ChatPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        "Hi there! I'm your business analyst — I can look up your PO data, RFQ pipeline, pricing, customer activity, and flag risks. Just ask me anything in plain English.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSql, setShowSql] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>(() => {
    const stored = localStorage.getItem('chat_session_id');
    if (stored) return stored;
    const id = crypto.randomUUID();
    localStorage.setItem('chat_session_id', id);
    return id;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg: Message = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const history = messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const { data } = await postChatQuery({
        question: text,
        history,
        session_id: sessionId,
      });

      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('chat_session_id', data.session_id);
      }

      const assistantMsg: Message = {
        role: "assistant",
        content: data.answer || "I couldn't find an answer to that.",
        sql: data.sql,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (caught) {
      // The chat route answers a failure with { answer } or { error }.
      const err = caught as { response?: { data?: { answer?: string; error?: string } } };
      const errorContent =
        err.response?.data?.answer ||
        err.response?.data?.error ||
        "Sorry, something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: errorContent },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <Fab
          color="primary"
          aria-label="AI Analyst"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1200,
            boxShadow: 4,
          }}
        >
          <ChatBubbleOutlineIcon />
        </Fab>
      )}

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420, lg: 460, xl: 540 },
            height: "100%",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            minHeight: 56,
            boxSizing: "border-box",
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyIcon />
            <Typography variant="h6" component="h2" fontWeight={600}>
              AI Analyst
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={() => setOpen(false)} size="small" aria-label="Close chat">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            bgcolor: "grey.50",
          }}
        >
          {messages.map((msg, idx) => (
            <ChatMessage
              key={idx}
              msg={msg}
              sqlOpen={showSql === `${idx}`}
              onToggleSql={() => setShowSql(showSql === `${idx}` ? null : `${idx}`)}
            />
          ))}

          {/* Typing indicator */}
          {loading && <TypingIndicator />}

          <div ref={messagesEndRef} />
        </Box>

        {/* Suggested prompts / Input */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {messages.length <= 1 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
              {SUGGESTED_PROMPTS.map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => sendMessage(prompt)}
                  sx={{
                    fontSize: "0.8125rem",
                    maxWidth: "100%",
                    "& .MuiChip-label": { whiteSpace: "normal", py: 0.75 },
                  }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask about your business..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              inputRef={inputRef}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim()}
              aria-label="Send message"
              sx={{ mb: 0.25 }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default ChatPanel;
